export const CATEGORY_LABELS = {
  technical_functional: 'Technical / Functional',
  cognitive: 'Cognitive',
  communication: 'Communication',
  behavioral: 'Behavioral',
  managerial_leadership: 'Managerial / Leadership',
  interview_performance: 'Interview performance',
};

export const SCORING_MODE_LABELS = {
  skill_rollup: 'Uses resume skills',
  targeted_question: 'Dedicated interview question',
  holistic: 'Scored from all answers',
};

export const IMPORTANCE_OPTIONS = [
  { value: 'critical', label: 'Critical' },
  { value: 'high', label: 'High' },
  { value: 'medium', label: 'Medium' },
  { value: 'low', label: 'Low' },
];

export const STATUS_LABELS = {
  off: 'Off',
  draft: 'Draft',
  ready: 'Ready',
};

export function categoryLabel(value) {
  return CATEGORY_LABELS[value] || value || '';
}

export function scoringModeLabel(value) {
  return SCORING_MODE_LABELS[value] || value || '';
}

export function importanceLabel(value) {
  const match = IMPORTANCE_OPTIONS.find((opt) => opt.value === value);
  return match ? match.label : value || '';
}

export function statusLabel(value) {
  return STATUS_LABELS[value] || 'Off';
}

export function bpsFromWeight(weight) {
  const n = Number(weight);
  if (!Number.isFinite(n) || n < 0) return 0;
  if (n > 100) return 10000;
  return Math.round(n * 100);
}

export function weightFromBps(bps) {
  return Math.round(((Number(bps) || 0) / 100) * 100) / 100;
}

export function clampWeight(value) {
  const n = Number(value);
  if (!Number.isFinite(n) || n < 0) return 0;
  if (n > 100) return 100;
  return Math.round(n * 100) / 100;
}

export function clampMinScore(value) {
  if (value === '' || value == null) return null;
  const n = Number(value);
  if (!Number.isFinite(n)) return null;
  if (n < 0) return 0;
  if (n > 10) return 10;
  return Math.round(n * 10) / 10;
}

export function toBenchmarkPayload(rows) {
  return (rows || []).map((row, index) => {
    const weight = clampWeight(row.weight ?? weightFromBps(row.weight_bps));
    return {
      id: row.id,
      definition_slug: row.definition_slug || row.slug || '',
      slug: row.slug || '',
      name: row.name || '',
      description: row.description || '',
      category: row.category || '',
      scoring_mode: row.scoring_mode || '',
      importance: row.importance || 'medium',
      weight,
      weight_bps: bpsFromWeight(weight),
      min_score: clampMinScore(row.min_score),
      evaluation_criteria: row.evaluation_criteria || '',
      sort_order: index,
      is_active: row.is_active !== false,
      is_ai_suggested: Boolean(row.is_ai_suggested),
    };
  });
}

export function splitJdFormAndBenchmarks(form) {
  const { benchmarks, benchmarks_approve, ...jdPayload } = form || {};
  return {
    jdPayload,
    benchmarks: Array.isArray(benchmarks) ? benchmarks : [],
    approve: Boolean(benchmarks_approve),
  };
}

export async function waitForBenchmarkSuggestions(api, initial, statusUrl) {
  let suggestions = initial?.suggestions;
  if (initial?.status === 'queued' && initial?.task_id) {
    for (let i = 0; i < 20; i += 1) {
      await new Promise((resolve) => setTimeout(resolve, 1500));
      const poll = await api.get(`${statusUrl}?task_id=${initial.task_id}`);
      if (poll.data.status === 'complete') {
        suggestions = poll.data.suggestions;
        break;
      }
      if (poll.data.status === 'failed') {
        throw new Error(poll.data.error || 'Could not suggest competency criteria.');
      }
    }
  }
  return suggestions;
}

export async function recommendBenchmarkRows(api, { jdId, jdFields }) {
  if (jdId) {
    const res = await api.post(`job-descriptions/${jdId}/benchmarks/recommend/`);
    return waitForBenchmarkSuggestions(
      api,
      res.data,
      `job-descriptions/${jdId}/benchmarks/recommend-status/`
    );
  }
  const res = await api.post('job-descriptions/benchmarks/preview-recommend/', jdFields || {});
  return waitForBenchmarkSuggestions(
    api,
    res.data,
    'job-descriptions/benchmarks/preview-recommend-status/'
  );
}

export async function saveJdBenchmarks(api, jdId, rows, { approve = false } = {}) {
  if (!jdId || !Array.isArray(rows) || rows.length === 0) return null;
  const res = await api.put(`job-descriptions/${jdId}/benchmarks/`, {
    benchmarks: toBenchmarkPayload(rows),
  });
  if (approve) {
    return api.post(`job-descriptions/${jdId}/benchmarks/approve/`);
  }
  return res;
}
